import React, { useEffect, useMemo, useState } from 'react';
import '../styles/blackListTable.scss';
import { Checkbox } from '@mui/material';
import ShortString from '../../../common-components/ShortString/ShortString';
import LinkIcon from '../../../Icons/icons/LinkIcon';
import BlacklistRowSkeleton from '../../../Skeletons/BlacklistRowSkeleton';
import { evictBlacklistAndRefetch, fetchBlacklistedInfluencer } from '../../../../redux/blacklist/blacklistActions';
import { useDispatch, useSelector } from 'react-redux';
import { blacklistFetchLimit } from '../../../../utils/constants';
import { formatFairFee, formatFollowers } from '../../../../utils/global';
import PaginationComponent from '../../components/PaginationComponent';
import ButtonComponent from '../../../common-components/ButtonComponent/ButtonComponent';
import UserMinus from '../../../Icons/icons/UserMinus';
import CustomTooltip from '../../../common-components/CustomTooltip/CustomTooltip';
import { useNavigate } from 'react-router-dom';
import { unblockInfluencers } from '../../../../services/blacklist.service';
import { NotificationManager } from 'react-notifications';

const BlacklistTable = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const { blacklistedInfluencer, isLoading, count } = useSelector((s) => s.blacklist);
  const [isUnblockLoading, setIsUnblockLoading] = useState(false);
  const currentKey = useMemo(() => `${blacklistFetchLimit}-${currentPage}`, [currentPage]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const canUnblock = useMemo(() => Boolean(selectAll || selectedIds.length), [selectAll, selectedIds]);
  const navigate = useNavigate();

  const blacklistInfluencerList = useMemo(
    () => blacklistedInfluencer[currentKey] || [],
    [blacklistedInfluencer, currentKey]
  );

  useEffect(() => {
    if (!blacklistedInfluencer[currentKey]) {
      dispatch(fetchBlacklistedInfluencer());
    }
  }, []);

  useEffect(() => {
    if (!blacklistInfluencerList?.length && !isLoading) {
      navigate('/dashboard/settings');
    }
  }, [blacklistInfluencerList, navigate, isLoading]);

  const onPageChange = (nextPage) => {
    if (nextPage > currentPage && blacklistedInfluencer?.[`${blacklistFetchLimit}-${nextPage}`] === undefined) {
      dispatch(
        fetchBlacklistedInfluencer({
          page: nextPage,
          cursor: blacklistInfluencerList[blacklistInfluencerList.length - 1]._id,
        })
      );
    }
    setCurrentPage(nextPage);
  };

  const handleUnblockInfluencers = async () => {
    const body = {};
    if (selectAll) {
      body['unblockAll'] = true;
    } else {
      body['influencerIds'] = selectedIds;
    }
    setIsUnblockLoading(true);
    const res = await unblockInfluencers(body);
    if (res.status) {
      dispatch(evictBlacklistAndRefetch());
      NotificationManager.success(`This creator is no longer blacklisted`, '', 3000);
    }
    setIsUnblockLoading(false);
  };

  return (
    <div>
      <div className="blacklist-influencer-table-header">
        <div className="left-text">
          {blacklistInfluencerList.length} out of {count} Creators
        </div>
        <div>
          <CustomTooltip show={!canUnblock} title="Select influencers to unblock." placement="bottom">
            <ButtonComponent
              variant="primary"
              icon={<UserMinus />}
              disabled={!canUnblock}
              onClick={() => handleUnblockInfluencers()}
              showLoading={isUnblockLoading}>
              Unblock
            </ButtonComponent>
          </CustomTooltip>
        </div>
      </div>
      <table className="blacklist-table-container">
        <tr>
          <th>
            <label>
              <Checkbox
                disableRipple
                checked={selectAll}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedIds([]);
                  }
                  setSelectAll(e.target.checked);
                }}
              />
            </label>
          </th>
          <th>Avatar & Name</th>
          <th>Username</th>
          <th>Followers</th>
          <th>Eng. Rate</th>
          <th>Growth Rate</th>
          <th>Est. Fair Fee</th>
          <th>Profile</th>
        </tr>
        {isLoading ? (
          <LoaderComponent />
        ) : (
          blacklistInfluencerList.map((profile) => (
            <BlackListTableItem
              key={profile._id}
              profile={profile}
              selectAll={selectAll}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
            />
          ))
        )}
      </table>
      {count > blacklistFetchLimit && !isLoading && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={Math.ceil(count / blacklistFetchLimit)}
          setCurrentPage={onPageChange}
        />
      )}
    </div>
  );
};

const BlackListTableItem = ({ profile, selectAll, selectedIds, setSelectedIds }) => {
  const isSelected = useMemo(
    () => Boolean(selectAll || selectedIds.find((id) => id === profile._id)),
    [selectAll, selectedIds, profile]
  );

  return (
    <tr>
      <td>
        <label>
          <Checkbox
            disableRipple
            checked={isSelected}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedIds((p) => [...p, profile._id]);
              } else {
                setSelectedIds((p) => p.filter((id) => id !== profile._id));
              }
            }}
          />
        </label>
      </td>
      <td>
        <div className="avatar-td">
          <img src={profile.profilePic} alt="profile" />
          <ShortString stringLength={30} placement="bottom" name={profile.name} />
        </div>
      </td>
      <td>@{profile.userName}</td>
      <td>{formatFollowers(profile.followers)}</td>
      <td>{(profile.engRate * 100).toFixed(2)}%</td>
      <td>{(profile.growthRate * 100).toFixed(2) + '%' || 'N/A'}</td>
      <td>{formatFairFee(profile?.fairPrice)}</td>
      <td>
        <a href={profile.profileLink} target="_blank" rel="noreferrer">
          <LinkIcon style={{ cursor: 'pointer' }} />
        </a>
      </td>
    </tr>
  );
};

const LoaderComponent = () => {
  return (
    <>
      <BlacklistRowSkeleton />
      <BlacklistRowSkeleton />
      <BlacklistRowSkeleton />
      <BlacklistRowSkeleton />
      <BlacklistRowSkeleton />
      <BlacklistRowSkeleton />
      <BlacklistRowSkeleton />
      <BlacklistRowSkeleton />
    </>
  );
};

export default BlacklistTable;
